import { Check, MagnifyingGlass, Plus } from "@phosphor-icons/react"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { EmptyState } from "@tokenterminal/core/ui/messaging/emptyState/EmptyState"
import { useMemo } from "react"
import { twMerge } from "tailwind-merge"
import { type ColumnItem } from "./ColumnSelectorModal"

// From the backend, we get the cohort analysis columns sorted like "Month 0,
// Month 1, Month 2, etc." instead of "Month 0, Month 1, Month 10, etc." This
// function sorts the columns correctly.
const sortCohortColumns = (filteredColumns: ColumnItem[]) => {
  const indexOfFirstMonthColumn = filteredColumns.findIndex((item) =>
    item.header_name?.match(/^Month \d+$/)
  )
  const baseColumns = filteredColumns.filter(
    (item) => !item.header_name?.match(/^Month \d+$/)
  )
  const monthColumns = filteredColumns
    .filter((item) => item.header_name?.match(/^Month \d+$/))
    .sort((a, b) => {
      const aNum = parseInt(a.header_name?.split(" ")[1] || "0")
      const bNum = parseInt(b.header_name?.split(" ")[1] || "0")
      return aNum - bNum
    })

  baseColumns.splice(indexOfFirstMonthColumn, 0, ...monthColumns)
  return baseColumns
}

const SelectableItemColumn: React.FC<React.PropsWithChildren> = ({
  children,
}) => (
  <div className="flex flex-col border border-solid border-border-default overflow-hidden rounded-lg">
    {children}
  </div>
)

export const SelectableItem = ({
  item,
  onSelect,
  isAdded,
}: {
  item: ColumnItem
  onSelect: () => void
  isAdded: boolean
}) => {
  return (
    <button
      className={twMerge(
        "flex items-center justify-between gap-2.5 cursor-pointer px-3 py-2 w-full border-b border-solid border-neutral-100 dark:border-neutral-800 last:border-b-0",
        isAdded
          ? "cursor-not-allowed"
          : "group/selectable-item hover:bg-neutral-50 dark:hover:bg-neutral-900"
      )}
      onClick={onSelect}
      disabled={isAdded}
    >
      <span className={isAdded ? "text-fg-secondary" : ""}>
        {item.header_name}
      </span>
      <div className="relative w-[80px] flex items-center justify-end h-4">
        {!isAdded && (
          <Icon
            as={Plus}
            className="w-4 h-4 text-fg-secondary opacity-0 group-hover/selectable-item:opacity-100 absolute"
          />
        )}
        {isAdded && (
          <div className="flex items-center gap-2 absolute top-0 right-0 text-xs text-fg-secondary">
            Added
            <Icon as={Check} className="w-3 h-3" />
          </div>
        )}
      </div>
    </button>
  )
}

export const SelectableColumnsList = ({
  columns,
  search,
  handleAddColumn,
  draftSelectedColumns,
}: {
  columns: ColumnItem[]
  search: string
  handleAddColumn: (field: string) => void
  draftSelectedColumns: string[]
}) => {
  const filteredColumns = useMemo(() => {
    return columns.filter((item) =>
      item.header_name?.toLowerCase().includes(search.toLowerCase())
    )
  }, [columns, search])

  // Some silly frontend sorting logic to ensure that the Cohort Analysis
  // columns sort correctly.
  const sortedColumns = useMemo(() => {
    if (
      filteredColumns.some((item) => item.header_name?.match(/^Month \d+$/))
    ) {
      return sortCohortColumns(filteredColumns)
    }
    return filteredColumns
  }, [filteredColumns])

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="flex flex-col gap-4">
        {sortedColumns.length > 0 && (
          <SelectableItemColumn>
            {sortedColumns.map((item) => {
              const isAdded =
                item.children.length > 0
                  ? item.children.every((child) =>
                      draftSelectedColumns.includes(child.field || "")
                    )
                  : draftSelectedColumns.includes(item.field || "")

              return (
                <SelectableItem
                  key={item.field || item.children[0].field}
                  item={item}
                  onSelect={() => {
                    if (item.children.length > 0) {
                      const nextUnselectedChild = item.children.find(
                        (child) =>
                          !draftSelectedColumns.includes(child.field || "")
                      )

                      if (nextUnselectedChild) {
                        handleAddColumn(nextUnselectedChild.field || "")
                      }
                    } else {
                      handleAddColumn(item.field || "")
                    }
                  }}
                  isAdded={isAdded}
                />
              )
            })}
          </SelectableItemColumn>
        )}
        {sortedColumns.length === 0 && (
          <EmptyState
            headingText="No columns found"
            bodyText="Try a different search"
            icon={MagnifyingGlass}
            size="small"
          />
        )}
      </div>
    </div>
  )
}
